import { useMemo } from 'react'
import { getRouteApi, useRouter } from '@tanstack/react-router'
import { mapTechnologyPages } from '../../utils/mapTechnologyPages'
import type { LeadFormRelation } from '../../types/strapi'
import type { SiteLayoutData } from '../../types/siteData'
import { LeadForm } from './LeadForm'
import { mapLeadForm } from './mapLeadForm'
import type { LeadFormConfig, LeadFormSurface } from './types'

const rootRoute = getRouteApi('__root__')

type CmsLeadFormProps = {
  leadForm?: LeadFormRelation | null
  surface?: LeadFormSurface
  productName?: string
  /** Product slug a campaign page's form returns to when afterSubmit is parentProduct. */
  parentProductSlug?: string
}

function usePublishedProductNames() {
  const { products } = rootRoute.useLoaderData() as Pick<
    SiteLayoutData,
    'products'
  >
  return useMemo(
    () => mapTechnologyPages(products?.data).map(({ value }) => value),
    [products],
  )
}

/** Renders the CMS Lead Form linked to a page, or nothing when none is set. */
export function CmsLeadForm({
  leadForm,
  surface,
  productName,
  parentProductSlug,
}: CmsLeadFormProps) {
  const router = useRouter()
  const productNames = usePublishedProductNames()
  const form = useMemo(
    () => mapLeadForm(leadForm?.data?.attributes, productNames),
    [leadForm, productNames],
  )

  if (!form) return null

  const afterSubmit = (config: LeadFormConfig) => {
    if (config.afterSubmit === 'redirect' && config.redirectUrl) {
      const href = config.redirectUrl
      return () => void router.navigate({ href })
    }
    if (config.afterSubmit === 'parentProduct' && parentProductSlug) {
      return () =>
        void router.navigate({
          to: '/products/$slug',
          params: { slug: parentProductSlug },
        })
    }
    return undefined
  }

  return (
    <LeadForm
      form={form}
      surface={surface}
      productName={productName}
      onSubmitted={afterSubmit(form)}
    />
  )
}
